<script setup>
import { ref } from 'vue';

const funds = ref([
  {
    name: 'Motilal Oswal S&P 500 Index Fund',
    sipStatus: 'Suspended',
    lumpsumStatus: 'Suspended',
    strategy: 'Currently at its internal limit. No fresh inflows allowed. Existing SIPs may continue but are being monitored.',
    statusColor: 'red'
  },
  {
    name: 'Edelweiss US Tech Equity FoF',
    sipStatus: 'Active',
    lumpsumStatus: 'Suspended',
    strategy: 'Accepting new SIPs (usually capped at ₹50,000/month). Lumpsum is blocked to avoid sudden limit breaches.',
    statusColor: 'yellow'
  },
  {
    name: 'Franklin India Feeder - US Opp.',
    sipStatus: 'Active',
    lumpsumStatus: 'Active',
    strategy: 'One of the few funds with headroom. Open for both routes, but with a daily monitoring cap.',
    statusColor: 'green'
  },
  {
    name: 'Kotak Nasdaq 100 FoF',
    sipStatus: 'Suspended',
    lumpsumStatus: 'Suspended',
    strategy: 'Passive funds are usually the first to hit limits. Currently closed for all new inflows.',
    statusColor: 'red'
  },
  {
    name: 'Mirae Asset NYSE FANG+ FoF',
    sipStatus: 'Active',
    lumpsumStatus: 'Suspended',
    strategy: 'Only SIPs allowed to manage the flow of capital steadily.',
    statusColor: 'yellow'
  }
]);

const getStatusIcon = (status) => {
  return status === 'Active' ? '🟢' : '🔴';
};
</script>

<template>
  <div class="feeder-funds">
    <h2 class="main-title">Feeder Funds / International Mutual Funds</h2>
    <p class="main-intro">
      SEBI-regulated domestic mutual funds that invest in international markets. You pay in INR, the AMC handles USD conversion.
    </p>

    <!-- Process Flow -->
    <div class="process-section">
      <h3 class="section-header">📋 Investment Process Flow</h3>
      <div class="process-grid">
        <!-- Step 1 -->
        <div class="process-card purple">
          <div class="step-number">1</div>
          <h4>🏦 Platform & Selection</h4>
          <div class="step-content">
            <p class="step-label">Platforms:</p>
            <p class="platforms">ICICI Direct | HDFC Sky | Groww | Zerodha Coin | AMC Websites</p>
            <p class="step-label">Popular Funds:</p>
            <ul class="fund-list">
              <li>Motilal Oswal S&P 500 Index Fund</li>
              <li>Edelweiss US Technology Equity FoF</li>
              <li>Franklin India Feeder - US Opportunities</li>
            </ul>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="process-card blue">
          <div class="step-number">2</div>
          <h4>✅ Onboarding & Purchase</h4>
          <div class="step-content">
            <p><strong>KYC:</strong> No additional KYC required</p>
            <p><strong>Payment:</strong> INR via UPI/Net Banking/Mandate</p>
            <p><strong>Currency:</strong> AMC handles USD conversion (you don't)</p>
            <p class="highlight">AMC pools INR from investors → Converts to USD → Invests in parent fund (US/Luxembourg)</p>
          </div>
        </div>

        <!-- Step 3 -->
        <div class="process-card green">
          <div class="step-number">3</div>
          <h4>💰 Redemption Process</h4>
          <div class="step-content">
            <p><strong>Action:</strong> Click "Redeem" on portal/app</p>
            <p><strong>Timeline:</strong> T+4 to T+5 business days (vs T+2 for domestic)</p>
            <p><strong>Reason:</strong> AMC waits for US fund to sell → USD → INR conversion</p>
            <p><strong>Payout:</strong> Credited to your domestic INR bank account</p>
          </div>
        </div>

        <!-- Step 4 -->
        <div class="process-card orange">
          <div class="step-number">4</div>
          <h4>🔓 Lock-in & Exit Loads</h4>
          <div class="step-content">
            <p><strong>Lock-in:</strong> No mandatory lock-in (unlike ELSS)</p>
            <p><strong>Exit Load:</strong> Typically 1% if redeemed within 30/90/365 days</p>
            <p><strong>After Period:</strong> Exit is free</p>
            <p class="note">⚠️ Check specific fund details for exact periods</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Critical Nuances -->
    <div class="nuances-section">
      <h3 class="section-header">⚠️ Critical Nuances (Watch-Outs)</h3>

      <div class="nuances-grid">
        <!-- Taxation -->
        <div class="nuance-card">
          <div class="nuance-icon">💸</div>
          <h4>A. Taxation (2026 onwards)</h4>
          <div class="tax-info">
            <div class="tax-row ltcg">
              <span class="tax-label">LTCG (>12 months):</span>
              <span class="tax-value">12.5%</span>
              <span class="tax-note">Above ₹1.25L exemption</span>
            </div>
            <div class="tax-row stcg">
              <span class="tax-label">STCG (<12 months):</span>
              <span class="tax-value">Slab Rate</span>
              <span class="tax-note">Added to income</span>
            </div>
          </div>
        </div>

        <!-- SEBI Limits -->
        <div class="nuance-card">
          <div class="nuance-icon">🚨</div>
          <h4>B. SEBI Investment Limits</h4>
          <p class="nuance-text">RBI sets $7 Billion total limit for entire Indian MF industry to invest abroad.</p>
          <div class="risk-box">
            <strong>⚠️ The Risk:</strong> SEBI occasionally hits this limit and pauses new investments.
          </div>
          <p class="watch-note">👀 Watch for "Status Indicator" showing if fund accepts new SIP/Lumpsum</p>
        </div>

        <!-- Tracking Error -->
        <div class="nuance-card">
          <div class="nuance-icon">📊</div>
          <h4>C. Tracking Error & Expense Ratio</h4>
          <div class="tracking-info">
            <p><strong>Double Expense:</strong></p>
            <ul>
              <li>Indian AMC fee</li>
              <li>Global Parent AMC fee</li>
            </ul>
            <p><strong>Tracking Error:</strong></p>
            <p class="small-text">Time zone differences + currency fluctuations = NAV may not perfectly match S&P 500 daily</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Fund Status Table -->
    <div class="fund-status-section">
      <h3 class="section-header">📈 Fund Status Monitor (Stop-Start Risk)</h3>
      <div class="fund-table">
        <div class="table-header">
          <div class="col-fund">Fund Name</div>
          <div class="col-status">SIP Status</div>
          <div class="col-status">Lumpsum Status</div>
          <div class="col-strategy">Strategy/Nuance</div>
        </div>

        <div
          v-for="(fund, index) in funds"
          :key="index"
          :class="['table-row', fund.statusColor]"
        >
          <div class="col-fund">{{ fund.name }}</div>
          <div class="col-status">
            <span :class="['status-badge', fund.sipStatus.toLowerCase()]">
              {{ getStatusIcon(fund.sipStatus) }} {{ fund.sipStatus }}
            </span>
          </div>
          <div class="col-status">
            <span :class="['status-badge', fund.lumpsumStatus.toLowerCase()]">
              {{ getStatusIcon(fund.lumpsumStatus) }} {{ fund.lumpsumStatus }}
            </span>
          </div>
          <div class="col-strategy">{{ fund.strategy }}</div>
        </div>
      </div>
    </div>

    <!-- Key Takeaways -->
    <div class="takeaways-section">
      <h3 class="section-header">💡 Key Takeaways</h3>
      <div class="takeaways-grid">
        <div class="takeaway-item">
          <i class="pi pi-check-circle"></i>
          <span>No additional KYC - Use existing MF platforms</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-check-circle"></i>
          <span>Pay in INR - AMC handles currency conversion</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-check-circle"></i>
          <span>Redemption takes T+4 to T+5 days</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-exclamation-triangle"></i>
          <span>Watch SEBI limits - Funds may suspend inflows</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-exclamation-triangle"></i>
          <span>Double expense ratio (Indian + Global AMC)</span>
        </div>
        <div class="takeaway-item">
          <i class="pi pi-exclamation-triangle"></i>
          <span>LTCG @ 12.5% | STCG @ Slab Rate (2026 onwards)</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.feeder-funds {
  max-width: 1400px;
  margin: 0 auto;
}

.main-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.main-intro {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  border-left: 3px solid #8b5cf6;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.section-header {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 2px solid #e5e7eb;
}

/* Process Flow */
.process-section {
  margin-bottom: 16px;
}

.process-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.process-card {
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  border: 2px solid;
}

.process-card.purple {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  border-color: #8b5cf6;
}

.process-card.blue {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-color: #3b82f6;
}

.process-card.green {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-color: #10b981;
}

.process-card.orange {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-color: #f59e0b;
}

.step-number {
  position: absolute;
  top: -10px;
  left: 10px;
  background: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.75rem;
  border: 2px solid currentColor;
}

.process-card h4 {
  font-size: 0.85rem;
  font-weight: 700;
  margin: 6px 0 8px 0;
  color: #1f2937;
}

.step-content {
  font-size: 0.75rem;
  line-height: 1.3;
}

.step-content p {
  margin: 6px 0;
  color: #374151;
}

.step-label {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px !important;
}

.platforms {
  font-size: 0.75rem;
  color: #4b5563;
  margin-bottom: 8px !important;
}

.fund-list {
  list-style: none;
  padding: 0;
  margin: 4px 0;
}

.fund-list li {
  padding: 3px 0;
  font-size: 0.75rem;
  color: #4b5563;
}

.highlight {
  background: rgba(255, 255, 255, 0.6);
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-top: 8px !important;
  font-style: italic;
}

.note {
  font-size: 0.75rem;
  font-style: italic;
  margin-top: 8px !important;
}

/* Nuances Section */
.nuances-section {
  margin-bottom: 16px;
}

.nuances-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.nuance-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.nuance-icon {
  font-size: 1.5rem;
  margin-bottom: 6px;
}

.nuance-card h4 {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.nuance-text {
  font-size: 0.8rem;
  color: #4b5563;
  line-height: 1.5;
  margin-bottom: 10px;
}

.tax-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tax-row {
  padding: 8px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tax-row.ltcg {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.tax-row.stcg {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.tax-label {
  font-weight: 600;
  font-size: 0.8rem;
  color: #1f2937;
}

.tax-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #065f46;
}

.tax-row.stcg .tax-value {
  color: #92400e;
}

.tax-note {
  font-size: 0.7rem;
  color: #6b7280;
  font-style: italic;
}

.risk-box {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  padding: 8px;
  border-radius: 6px;
  border-left: 3px solid #ef4444;
  font-size: 0.8rem;
  margin: 8px 0;
}

.watch-note {
  font-size: 0.75rem;
  color: #4b5563;
  font-style: italic;
  margin-top: 8px;
}

.tracking-info {
  font-size: 0.8rem;
}

.tracking-info p {
  margin: 6px 0;
  color: #374151;
}

.tracking-info ul {
  margin: 4px 0;
  padding-left: 20px;
}

.tracking-info li {
  font-size: 0.75rem;
  color: #4b5563;
  margin: 2px 0;
}

.small-text {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
}

/* Fund Status Table */
.fund-status-section {
  margin-bottom: 16px;
}

.fund-table {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 3fr;
  gap: 10px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  font-weight: 700;
  font-size: 0.75rem;
  color: #1f2937;
  border-bottom: 2px solid #d1d5db;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 3fr;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.75rem;
  transition: background 0.2s;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: #f9fafb;
}

.table-row.green {
  border-left: 4px solid #10b981;
}

.table-row.yellow {
  border-left: 4px solid #f59e0b;
}

.table-row.red {
  border-left: 4px solid #ef4444;
}

.col-fund {
  font-weight: 600;
  color: #1f2937;
}

.col-status {
  display: flex;
  align-items: center;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-badge.active {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.suspended {
  background: #fee2e2;
  color: #7f1d1d;
}

.col-strategy {
  color: #4b5563;
  line-height: 1.4;
}

/* Key Takeaways */
.takeaways-section {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  padding: 16px;
  border-radius: 8px;
  border: 2px solid #bae6fd;
}

.takeaways-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.takeaway-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #374151;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.takeaway-item i {
  font-size: 1rem;
  flex-shrink: 0;
}

.takeaway-item .pi-check-circle {
  color: #10b981;
}

.takeaway-item .pi-exclamation-triangle {
  color: #f59e0b;
}

@media (max-width: 1200px) {
  .process-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .nuances-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .process-grid {
    grid-template-columns: 1fr;
  }

  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .col-status {
    justify-content: flex-start;
  }

  .takeaways-grid {
    grid-template-columns: 1fr;
  }
}
</style>
